import { createFileRoute, Link } from "@tanstack/react-router";
import { UtensilsCrossed, Coffee, Pizza, Phone } from "lucide-react";
import { breadcrumbSchema, canonical, faqPageSchema } from "@/lib/seo";

export const Route = createFileRoute("/blog/restaurants-near-crystal-beach")({
  head: () => ({
    meta: [
      { title: "Places to Eat Near Crystal Beach - Malak BnB Resort" },
      { name: "description", content: "Where to eat near Crystal Beach and Ridgeway, Ontario, from casual pizza to a slower dinner out. Stay at Malak BnB Resort, five minutes from most of it." },
      { property: "og:title", content: "Places to Eat Near Crystal Beach - Malak BnB Resort" },
      { property: "og:description", content: "A dining guide to Crystal Beach and Ridgeway, from breakfast spots to a proper dinner out." },
      { property: "og:url", content: "https://malakbnbresort.ca/blog/restaurants-near-crystal-beach" },
      { property: "og:image", content: "/images/dining-out.webp" },
      { "script:ld+json": faqPageSchema(FAQ) },
      {
        "script:ld+json": breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Blog", path: "/blog" },
          { name: "Places to Eat Near Crystal Beach", path: "/blog/restaurants-near-crystal-beach" },
        ]),
      },
    ],
    links: [canonical("/blog/restaurants-near-crystal-beach")],
  }),
  component: RestaurantsPage,
});

const FAQ = [
  {
    q: "What restaurants are near Malak BnB Resort in Crystal Beach?",
    a: "Crystal Beach and nearby Ridgeway have a good spread of casual restaurants within a short walk or drive of Malak BnB Resort, including pizza, breakfast spots, and Italian dining. The exact walk or drive time depends on the restaurant.",
  },
  {
    q: "Is breakfast included at Malak BnB Resort, or should I plan to eat out?",
    a: "A continental breakfast buffet is included with every stay at Malak BnB Resort, so mornings are covered. Guests typically eat out for lunch and dinner while exploring Crystal Beach and Ridgeway.",
  },
  {
    q: "Are there casual dining options within walking distance of Bay Beach?",
    a: "Yes. Several restaurants and cafes in Crystal Beach sit close to Bay Beach and the town centre, an easy walk from Malak BnB Resort for lunch or an early dinner after a beach day.",
  },
  {
    q: "Do I need a car to reach restaurants near Crystal Beach?",
    a: "Not for the town centre itself, which is walkable from Malak BnB Resort. A short drive opens up more options in Ridgeway and Fort Erie for guests who want a wider choice.",
  },
];

function RestaurantsPage() {
  return (
    <div>
      {/* Hero */}
      <section className="relative h-[55vh] min-h-[380px] overflow-hidden">
        <img
          src="/images/dining-out.webp"
          alt="A restaurant table set for dinner"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground/65 via-foreground/20 to-transparent" />
        <div className="relative z-10 flex h-full items-end pb-16 px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl w-full">
            <div className="flex items-center gap-2 text-white/70 text-sm mb-3">
              <Link to="/blog" className="hover:text-white transition-colors">Blog</Link>
              <span>/</span>
              <span>Places to Eat</span>
            </div>
            <p className="text-white/70 font-semibold text-sm uppercase tracking-widest mb-3">A Dining Guide</p>
            <h1 className="text-4xl sm:text-5xl font-heading text-white leading-tight max-w-2xl">
              Places to Eat Near Crystal Beach
            </h1>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg text-muted-foreground space-y-5 leading-relaxed">
            <p>
              Crystal Beach and neighbouring Ridgeway punch above their size when it comes to food. It is a small
              area, but there is a genuine spread of options: casual pizza places, breakfast spots that get busy on
              summer mornings, and a few restaurants worth dressing up slightly for. None of it is far from Malak
              BnB Resort.
            </p>
            <p>
              This is not an exhaustive list, since restaurants change hands and hours shift by season, but it is a
              solid starting point for a weekend in the area.
            </p>
          </div>
        </div>
      </section>

      {/* Casual */}
      <section className="pb-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 mb-3">
            <Pizza className="h-5 w-5 text-primary" />
            <p className="text-primary font-semibold text-sm uppercase tracking-wider">Casual and Quick</p>
          </div>
          <h2 className="text-2xl sm:text-3xl font-heading text-foreground leading-snug mb-4">
            Pizza and Easy Lunches
          </h2>
          <p className="text-muted-foreground leading-relaxed">
            Mabel's Gourmet Pizza is a local favourite for a casual meal, known for pizzas generous enough to share
            across a table. It is the kind of place that works well after a day at Bay Beach, when nobody wants a
            formal sit-down dinner.
          </p>
        </div>
      </section>

      {/* Breakfast */}
      <section className="py-20 bg-card">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 mb-3">
            <Coffee className="h-5 w-5 text-primary" />
            <p className="text-primary font-semibold text-sm uppercase tracking-wider">Breakfast and Brunch</p>
          </div>
          <h2 className="text-2xl sm:text-3xl font-heading text-foreground leading-snug mb-4">
            For the Mornings You Want to Eat Out
          </h2>
          <p className="text-muted-foreground leading-relaxed">
            Shaka Farm to Beach has built a reputation locally for a relaxed, beach-casual breakfast, a good option
            on a morning when you want to sit outside before heading to the water. Malak BnB Resort includes
            breakfast with every stay, so this is really for the days you want a change of scenery.
          </p>
        </div>
      </section>

      {/* Dinner out */}
      <section className="py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 mb-3">
            <UtensilsCrossed className="h-5 w-5 text-primary" />
            <p className="text-primary font-semibold text-sm uppercase tracking-wider">A Slower Dinner</p>
          </div>
          <h2 className="text-2xl sm:text-3xl font-heading text-foreground leading-snug mb-4">
            For the Nights You Want to Sit and Stay Awhile
          </h2>
          <p className="text-muted-foreground leading-relaxed">
            Amalfi's Trattoria & Bar and Casa Hugo both come up often in local recommendations for a proper dinner
            out, with a warmer, more sit-down atmosphere than the beachfront spots. Either is a good choice for a
            Saturday night after a full day in Crystal Beach.
          </p>
        </div>
      </section>

      {/* CTA link back */}
      <section className="pb-4 bg-card pt-0">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 text-center pb-20">
          <p className="text-muted-foreground leading-relaxed mb-4">
            Planning the rest of the weekend around it?
          </p>
          <Link to="/blog/weekend-getaway-crystal-beach-lake-erie" className="text-primary font-semibold text-sm hover:underline">
            See the full weekend getaway itinerary →
          </Link>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-primary font-semibold text-sm uppercase tracking-wider mb-3">Common Questions</p>
            <h2 className="text-3xl font-heading text-foreground">Eating Near Crystal Beach</h2>
          </div>
          <div className="space-y-8">
            {FAQ.map(({ q, a }) => (
              <div key={q} className="border-b border-border pb-8 last:border-0 last:pb-0">
                <h3 className="font-heading text-lg text-foreground mb-3">{q}</h3>
                <p className="text-muted-foreground leading-relaxed text-sm">{a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-foreground">
        <div className="mx-auto max-w-3xl px-4 text-center">
          <p className="text-primary-foreground/50 font-semibold text-sm uppercase tracking-widest mb-4">Crystal Beach, Ontario</p>
          <h2 className="text-3xl sm:text-4xl font-heading text-primary-foreground leading-tight">
            Stay close to all of it at Malak BnB Resort
          </h2>
          <p className="mt-5 text-primary-foreground/70 max-w-lg mx-auto leading-relaxed">
            Submit a reservation request and the team will personally confirm availability and send payment details directly.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link to="/book" className="inline-flex items-center justify-center rounded-full bg-primary px-10 py-4 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors shadow-lg">
              Request Your Stay
            </Link>
            <a href="tel:+12899298055" className="inline-flex items-center gap-2 text-primary-foreground/70 hover:text-primary-foreground text-sm font-medium transition-colors">
              <Phone className="h-4 w-4" />
              1-289-929-8055
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
